/**
 * Tabs — a keyboard-reachable tab strip with optional phase grouping.
 *
 * 352 activeTab implementations live across 35 files, and exactly ONE file in
 * src/ uses `role="tab"` at all. So almost every tab strip in the app is a row
 * of buttons: a screen reader is never told it is a tab set, and arrow keys do
 * nothing. That is the gap this closes — the styling was never the hard part.
 *
 * ── Groups ────────────────────────────────────────────────────────────────
 * `groups` exists because a long strip is not a flat list. Session detail
 * carries fifteen tabs that are really five phases (configure → launch →
 * outcomes → record), and drawing that structure is what makes fifteen tabs
 * legible instead of overwhelming. A single unnamed group renders as a plain
 * strip, so the simple case stays simple.
 *
 * Roving tabindex per the WAI-ARIA tabs pattern: only the active tab is in the
 * tab order, and ←/→/Home/End move between them.
 */
import React, { useRef } from 'react';
import { FOCUS_RING } from '../mono/primitives';

export interface TabItem<Id extends string = string> {
  id: Id;
  label: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  /** Small number beside the label. Omit rather than passing 0 — a zero count
      reads as a defect on a tab that is simply empty. */
  count?: number;
  disabled?: boolean;
}

export interface TabGroup<Id extends string = string> {
  /** Rendered above the group. A single group with no label draws no header. */
  label?: React.ReactNode;
  /** Tailwind text-colour class tinting this group's inactive icons. */
  tone?: string;
  tabs: TabItem<Id>[];
}

export interface TabsProps<Id extends string = string> {
  groups: TabGroup<Id>[];
  active: Id;
  onChange: (id: Id) => void;
  /** Accessible name for the tab set. */
  ariaLabel?: string;
  className?: string;
}

export function Tabs<Id extends string = string>({
  groups,
  active,
  onChange,
  ariaLabel,
  className = '',
}: TabsProps<Id>) {
  const stripRef = useRef<HTMLDivElement>(null);
  const flat = groups.flatMap((g) => g.tabs).filter((t) => !t.disabled);
  const showGroupLabels = groups.some((g) => g.label && g.tabs.length > 1);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = flat.findIndex((t) => t.id === active);
    if (i === -1) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % flat.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + flat.length) % flat.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = flat.length - 1;
    if (next === -1) return;
    e.preventDefault();
    onChange(flat[next].id);
    stripRef.current?.querySelector<HTMLButtonElement>(`[data-tab-id="${flat[next].id}"]`)?.focus();
  };

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`flex items-end gap-5 overflow-x-auto border-b ${className}`}
      style={{ borderColor: 'var(--rule)' }}
    >
      {groups.map((group, gi) => (
        <div key={group.label ? String(group.label) : gi} className="flex shrink-0 flex-col">
          {showGroupLabels && (
            <span
              className={`px-4 pb-0.5 pt-2 font-mono text-[0.62rem] uppercase tracking-[0.12em] ${
                group.tone ?? 'text-[var(--text-dim)]'
              }`}
            >
              {group.label}
            </span>
          )}
          <div className={`flex items-center ${showGroupLabels && !group.label ? 'pt-[1.35rem]' : ''}`}>
            {group.tabs.map((tab) => {
              const selected = tab.id === active;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`tab-${tab.id}`}
                  data-tab-id={tab.id}
                  aria-selected={selected}
                  aria-controls={`tabpanel-${tab.id}`}
                  tabIndex={selected ? 0 : -1}
                  disabled={tab.disabled}
                  onClick={() => onChange(tab.id)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 font-mono text-[0.72rem] uppercase tracking-[0.1em] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    selected
                      ? 'text-[var(--text-pri)]'
                      : 'text-[var(--text-ter)] hover:text-[var(--text-pri)]'
                  } ${FOCUS_RING}`}
                  /* Inline: the unlayered `.dark * { border-color }` rule would
                     otherwise flatten the active underline to the hairline grey. */
                  style={{ borderBottomColor: selected ? 'var(--mustard)' : 'transparent' }}
                >
                  {Icon && (
                    <Icon
                      className={`h-3.5 w-3.5 ${selected ? '' : group.tone ?? ''}`}
                    />
                  )}
                  {tab.label}
                  {typeof tab.count === 'number' && (
                    <span className="text-[var(--text-dim)]">{tab.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface TabPanelProps {
  /** Must match the TabItem id it belongs to. */
  id: string;
  active: string;
  children: React.ReactNode;
  className?: string;
}

/** Renders nothing unless its id is active, and wires the aria relationship
    back to its tab. Use it so the panel is announced as part of the tab set. */
export function TabPanel({ id, active, children, className = '' }: TabPanelProps) {
  if (id !== active) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}
